import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getReviewAccounts, updateAccountGoal, getReviews, getReview, generateReview,
  updateReviewSection, reviseReviewSection, applyReviewChanges, finishReview, deleteReview, reviewPdfUrl,
} from '../api';
import { Sparkles, Star, FileDown, CheckCircle2, Trash2, Send, Search, RefreshCw, AlertTriangle, Pencil, Check, X } from 'lucide-react';
import { ProposalStack } from '../components/ai';
import './ExecutiveReview.css';

// The weekly executive review.
//
// Pick the accounts (key accounts are pre-ticked), pick the audience, click
// Generate. The AI writes 3–4 bullets per account from the record, plus a
// one-glance top. Refine any account by telling it what to change; when the
// change implies a change to the account itself — the goal, an action item —
// it asks before touching anything. Finish saves each account's weekly
// summary and fixes the report; Download gives the PDF.

const STATUS = {
  on_track: { label: 'On track', className: 'badge-success' },
  at_risk: { label: 'At risk', className: 'badge-warning' },
  stalled: { label: 'Stalled', className: 'badge-gray' },
  needs_decision: { label: 'Needs decision', className: 'badge-danger' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status || '—', className: 'badge-gray' };
  return <span className={`badge ${s.className}`}>{s.label}</span>;
};

const weekLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const describeChange = (c) => {
  if (c.action === 'complete') return `Mark “${c.title}” complete`;
  if (c.action === 'redate') return `Move “${c.title}” to ${c.due_date}`;
  return `Add action item “${c.title}”${c.due_date ? ` due ${c.due_date}` : ''}`;
};

export default function ExecutiveReview() {
  const { user, hasPermission } = useAuth();
  const isManager = user?.role_name === 'Admin' || hasPermission('view_all_accounts') || hasPermission('manage_settings');

  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState('');
  const [audience, setAudience] = useState('internal');
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, r] = await Promise.all([getReviewAccounts(), getReviews(15)]);
      setAccounts(a.data);
      setReports(r.data);
      setSelected((current) => (current.size ? current : new Set(a.data.filter((x) => x.is_key_account).map((x) => x.id))));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load accounts.');
    }
  }, []);

  useEffect(() => { if (isManager) load(); }, [isManager, load]);

  const visibleAccounts = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? accounts.filter((a) => a.name.toLowerCase().includes(q)) : accounts;
  }, [accounts, filter]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleKey = async (account) => {
    try {
      const { data } = await updateAccountGoal(account.id, { is_key_account: !account.is_key_account });
      setAccounts((list) => list.map((a) => (a.id === account.id ? { ...a, is_key_account: data.is_key_account } : a)));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change the key-account flag.');
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const { data } = await generateReview([...selected], audience);
      setReport(data);
      setReports((list) => [{ id: data.id, week_of: data.week_of, audience: data.audience, title: data.title, status: data.status, created_at: data.created_at, created_by_name: data.created_by_name, account_count: data.accounts.length }, ...list]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not generate the review.');
    } finally {
      setGenerating(false);
    }
  };

  const open = async (id) => {
    try {
      const { data } = await getReview(id);
      setReport(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not open that report.');
    }
  };

  const finish = async () => {
    try {
      const { data } = await finishReview(report.id);
      setReport(data);
      setReports((list) => list.map((r) => (r.id === data.id ? { ...r, status: 'final' } : r)));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not finish the report.');
    }
  };

  const discard = async () => {
    if (!window.confirm('Discard this draft? Nothing has been written to any account.')) return;
    try {
      await deleteReview(report.id);
      setReports((list) => list.filter((r) => r.id !== report.id));
      setReport(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not discard the draft.');
    }
  };

  const replaceSection = (section, extra = {}) => {
    setReport((r) => ({ ...r, accounts: r.accounts.map((a) => (a.account_id === section.account_id ? { ...a, ...section, ...extra } : a)) }));
  };

  if (!isManager) {
    return (
      <div className="er-page">
        <div className="card"><div className="card-body"><p>The executive review is for managers.</p></div></div>
      </div>
    );
  }

  return (
    <div className="er-page">
      <div className="er-header">
        <div>
          <h1 className="er-title"><Sparkles size={22} /> Executive Review</h1>
          <p className="er-subtitle">A few true bullets per key account, written from the record for the week. Nothing is saved to an account until you finish.</p>
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div className="er-layout">
        <aside className="er-side">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Accounts</h3></div>
            <div className="card-body">
              <div className="search-input" style={{ marginBottom: 10 }}>
                <Search size={16} className="search-input-icon" aria-hidden="true" />
                <input type="text" className="form-input" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter accounts" />
              </div>
              <ul className="er-account-list">
                {visibleAccounts.map((a) => (
                  <li key={a.id} className={selected.has(a.id) ? 'selected' : ''}>
                    <label className="er-account">
                      <input type="checkbox" className="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} aria-label={`Include ${a.name}`} />
                      <span className="er-account-name">{a.name}</span>
                      <button type="button" className={`er-star ${a.is_key_account ? 'on' : ''}`} onClick={() => toggleKey(a)} title={a.is_key_account ? 'Key account — click to unmark' : 'Mark as key account'} aria-label={`${a.is_key_account ? 'Unmark' : 'Mark'} ${a.name} as key account`} aria-pressed={!!a.is_key_account}>
                        <Star size={14} />
                      </button>
                    </label>
                    <div className="er-account-meta">
                      {a.goal ? <span title={a.goal}>Goal set</span> : <span className="er-muted">No goal</span>}
                      {a.open_tasks > 0 && <span> · {a.open_tasks} open</span>}
                      {a.last_summary_week && <span> · last {weekLabel(a.last_summary_week)}</span>}
                    </div>
                  </li>
                ))}
                {visibleAccounts.length === 0 && <li className="er-muted">No accounts match.</li>}
              </ul>
            </div>
            <div className="card-body er-generate">
              <div className="er-audience" role="radiogroup" aria-label="Audience">
                <button type="button" className={audience === 'internal' ? 'active' : ''} onClick={() => setAudience('internal')} role="radio" aria-checked={audience === 'internal'}>Internal</button>
                <button type="button" className={audience === 'customer' ? 'active' : ''} onClick={() => setAudience('customer')} role="radio" aria-checked={audience === 'customer'}>Customer-facing</button>
              </div>
              <button type="button" className="btn btn-primary" onClick={generate} disabled={generating || selected.size === 0} style={{ width: '100%' }}>
                {generating ? <><RefreshCw size={16} className="er-spin" /> Writing {selected.size} account{selected.size === 1 ? '' : 's'}…</> : <><Sparkles size={16} /> Generate for {selected.size} account{selected.size === 1 ? '' : 's'}</>}
              </button>
              <p className="er-hint">{selected.size + 1} AI call{selected.size ? 's' : ''} · {audience === 'customer' ? 'no risks, no figures' : 'candid, for our executives'}</p>
            </div>
          </div>

          {reports.length > 0 && (
            <div className="card">
              <div className="card-header"><h3 className="card-title">Recent</h3></div>
              <ul className="er-recent">
                {reports.map((r) => (
                  <li key={r.id}>
                    <button type="button" className={report?.id === r.id ? 'active' : ''} onClick={() => open(r.id)}>
                      <span>{weekLabel(r.week_of)} · {r.audience === 'customer' ? 'Customer' : 'Internal'} · {r.account_count} account{r.account_count === 1 ? '' : 's'}</span>
                      <span className={`badge ${r.status === 'final' ? 'badge-success' : 'badge-gray'}`}>{r.status === 'final' ? 'Final' : 'Draft'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        <main className="er-main">
          {!report ? (
            <div className="card"><div className="card-body er-empty">
              <Sparkles size={40} className="er-muted" />
              <h3>No report open</h3>
              <p>Choose the accounts on the left and click Generate, or open a recent report.</p>
            </div></div>
          ) : (
            <ReportView
              report={report}
              onFinish={finish}
              onDiscard={discard}
              onSection={replaceSection}
              onError={setError}
              onAccountChange={load}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function ReportView({ report, onFinish, onDiscard, onSection, onError, onAccountChange }) {
  const draft = report.status === 'draft';
  return (
    <div className="er-report">
      <div className="card">
        <div className="card-header er-report-header">
          <div>
            <h2 className="er-report-title">{report.title}</h2>
            <p className="er-muted">
              {report.audience === 'customer' ? 'Customer-facing' : 'Internal'} · {draft ? 'Draft — nothing saved to accounts yet' : `Final · finished ${new Date(report.finished_at).toLocaleDateString()}`}
              {report.created_by_name && ` · by ${report.created_by_name}`}
            </p>
          </div>
          <div className="er-actions">
            <a className="btn btn-secondary" href={reviewPdfUrl(report.id)} target="_blank" rel="noopener noreferrer"><FileDown size={16} /> PDF</a>
            {draft && <button type="button" className="btn btn-secondary" onClick={onDiscard}><Trash2 size={16} /> Discard</button>}
            {draft && <button type="button" className="btn btn-primary" onClick={onFinish}><CheckCircle2 size={16} /> Finish &amp; save to accounts</button>}
          </div>
        </div>
        {(report.overview.bullets.length > 0 || report.overview.accounts.length > 0) && (
          <div className="card-body">
            <h4 className="er-label">At a glance</h4>
            <ul className="er-bullets">{report.overview.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
            <table className="table er-status-table">
              <tbody>
                {report.overview.accounts.map((a) => (
                  <tr key={a.account_id}>
                    <td className="er-status-name">{a.name}</td>
                    <td><StatusBadge status={a.status} /></td>
                    <td className="er-muted">{a.line}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {report.accounts.map((section) => (
        <SectionCard key={section.account_id} report={report} section={section} draft={draft} onSection={onSection} onError={onError} onAccountChange={onAccountChange} />
      ))}
    </div>
  );
}

function SectionCard({ report, section, draft, onSection, onError, onAccountChange }) {
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposed, setProposed] = useState(null);     // { goal, task_changes }
  const [ticks, setTicks] = useState({});
  const [applied, setApplied] = useState(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState({ summary: '', what_changed: '', risks: '' });
  const [goalEdit, setGoalEdit] = useState(null);
  const internal = report.audience === 'internal';

  const revise = async (e) => {
    e.preventDefault();
    if (!instruction.trim()) return;
    setBusy(true);
    setApplied(null);
    try {
      const { data } = await reviseReviewSection(report.id, section.account_id, instruction.trim());
      onSection(data.section);
      const changes = data.proposed || {};
      if (changes.goal || (changes.task_changes && changes.task_changes.length)) {
        setProposed(changes);
        const t = {};
        if (changes.goal) t.goal = true;
        (changes.task_changes || []).forEach((_, i) => { t[`t${i}`] = true; });
        setTicks(t);
      } else {
        setProposed(null);
      }
      setInstruction('');
    } catch (err) {
      onError(err.response?.data?.error || 'Could not revise this section.');
    } finally {
      setBusy(false);
    }
  };

  const apply = async (yes) => {
    if (!yes) { setProposed(null); return; }
    setBusy(true);
    try {
      const payload = {
        goal: ticks.goal ? proposed.goal : null,
        task_changes: (proposed.task_changes || []).filter((_, i) => ticks[`t${i}`]),
      };
      const { data } = await applyReviewChanges(report.id, section.account_id, payload);
      onSection(data.section);
      setApplied(data.applied);
      setProposed(null);
      onAccountChange();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not update the account.');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setText({ summary: section.summary.join('\n'), what_changed: section.what_changed.join('\n'), risks: section.risks.join('\n') });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const lines = (s) => s.split('\n').map((l) => l.trim()).filter(Boolean);
      const { data } = await updateReviewSection(report.id, section.account_id, { summary: lines(text.summary), what_changed: lines(text.what_changed), risks: lines(text.risks) });
      onSection(data);
      setEditing(false);
    } catch (err) {
      onError(err.response?.data?.error || 'Could not save the edit.');
    } finally {
      setBusy(false);
    }
  };

  const saveGoal = async (value) => {
    setBusy(true);
    try {
      const { data } = await updateAccountGoal(section.account_id, { goal: value });
      onSection({ account_id: section.account_id }, { goal: data.goal, suggested_goal: null });
      setGoalEdit(null);
      onAccountChange();
    } catch (err) {
      onError(err.response?.data?.error || 'Could not save the goal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card er-section" id={`account-${section.account_id}`}>
      <div className="card-header er-section-header">
        <h3 className="er-section-title">{section.name}</h3>
        <StatusBadge status={section.account_status} />
      </div>
      <div className="card-body">
        <div className="er-goal">
          <span className="er-label">Goal</span>
          {goalEdit !== null ? (
            <form className="er-goal-edit" onSubmit={(e) => { e.preventDefault(); saveGoal(goalEdit); }}>
              <input type="text" className="form-input" value={goalEdit} onChange={(e) => setGoalEdit(e.target.value)} maxLength={500} autoFocus aria-label={`Goal for ${section.name}`} />
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy}><Check size={14} /> Save to account</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGoalEdit(null)}><X size={14} /></button>
            </form>
          ) : (
            <p className={section.goal ? '' : 'er-muted'}>
              {section.goal || 'No goal set.'}
              {draft && <button type="button" className="er-inline" onClick={() => setGoalEdit(section.goal || '')} aria-label={`Edit goal for ${section.name}`}><Pencil size={13} /> {section.goal ? 'Edit' : 'Set a goal'}</button>}
            </p>
          )}
          {!section.goal && section.suggested_goal && draft && (
            <div className="er-suggest">
              <span><Sparkles size={13} /> Suggested from the record: <em>{section.suggested_goal}</em></span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => saveGoal(section.suggested_goal)} disabled={busy}>Use as the goal</button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="er-edit">
            <label className="er-label" htmlFor={`sum-${section.account_id}`}>Summary — one bullet per line</label>
            <textarea id={`sum-${section.account_id}`} className="form-input" rows={4} value={text.summary} onChange={(e) => setText({ ...text, summary: e.target.value })} />
            <label className="er-label" htmlFor={`chg-${section.account_id}`}>What changed this week</label>
            <textarea id={`chg-${section.account_id}`} className="form-input" rows={3} value={text.what_changed} onChange={(e) => setText({ ...text, what_changed: e.target.value })} />
            {internal && (
              <>
                <label className="er-label" htmlFor={`rsk-${section.account_id}`}>Risks &amp; blockers</label>
                <textarea id={`rsk-${section.account_id}`} className="form-input" rows={3} value={text.risks} onChange={(e) => setText({ ...text, risks: e.target.value })} />
              </>
            )}
            <div className="er-actions">
              <button type="button" className="btn btn-primary btn-sm" onClick={saveEdit} disabled={busy}><Check size={14} /> Save</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <h4 className="er-label">Summary {draft && <button type="button" className="er-inline" onClick={startEdit} aria-label={`Edit the text for ${section.name}`}><Pencil size={13} /> Edit text</button>}</h4>
            <ul className="er-bullets">{section.summary.map((b, i) => <li key={i}>{b}</li>)}</ul>
            {section.what_changed.length > 0 && (
              <>
                <h4 className="er-label">What changed this week</h4>
                <ul className="er-bullets">{section.what_changed.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </>
            )}
            {internal && section.risks.length > 0 && (
              <>
                <h4 className="er-label er-risk"><AlertTriangle size={13} /> Risks &amp; blockers</h4>
                <ul className="er-bullets er-risk">{section.risks.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </>
            )}
          </>
        )}

        <h4 className="er-label">Open action items</h4>
        {section.open_items.length ? (
          <ul className="er-items">
            {section.open_items.map((t) => (
              <li key={t.id}><span>{t.title}</span><span className="er-muted">{[t.due_date && `due ${t.due_date}`, t.assigned_to].filter(Boolean).join(' · ')}</span></li>
            ))}
          </ul>
        ) : <p className="er-muted">None open.</p>}

        {applied && applied.length > 0 && (
          <div className="er-applied" role="status">
            <CheckCircle2 size={14} /> Account updated: {applied.map((a) => a.type === 'goal' ? 'goal set' : a.type === 'task_complete' ? `“${a.title}” completed` : a.type === 'task_create' ? `“${a.title}” added` : `“${a.title}” moved to ${a.due_date}`).join('; ')}.
          </div>
        )}

        {draft && proposed && (
          <ProposalStack
            title="Also update the account?"
            intro={`The text is revised. Your instruction also implies these changes to ${section.name}'s record — tick the ones to make.`}
            ariaLabel={`Also update ${section.name}?`}
            items={[
              ...(proposed.goal ? [{ id: 'goal', kind: 'goal', title: `Set the goal to “${proposed.goal}”`, accepted: !!ticks.goal }] : []),
              ...(proposed.task_changes || []).map((c, i) => ({ id: `t${i}`, kind: 'task', title: describeChange(c), reason: c.reason, accepted: !!ticks[`t${i}`] })),
            ]}
            onToggle={(id, accepted) => setTicks({ ...ticks, [id]: accepted })}
            onConfirm={() => apply(true)}
            onDismiss={() => apply(false)}
            confirmLabel="Yes, update the account"
            dismissLabel="No, just the text"
            busy={busy}
          />
        )}

        {draft && (
          <form className="er-prompt" onSubmit={revise}>
            <input type="text" className="form-input" value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder={`Change ${section.name}'s section… e.g. "mention the thermal data risk", "we sent the samples Tuesday", "shorter"`} aria-label={`Instruction for ${section.name}`} disabled={busy} maxLength={1000} />
            <button type="submit" className="btn btn-primary" disabled={busy || !instruction.trim()}>{busy ? <RefreshCw size={16} className="er-spin" /> : <Send size={16} />} Revise</button>
          </form>
        )}
      </div>
    </div>
  );
}
